import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";

interface DevToolsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function Icon(component: any, className?: string) {
  return React.createElement(
    component as React.ComponentType<{ className?: string }>,
    { className }
  );
}

const terminalLines = [
  { type: "system", text: "[SYSTEM] Security module initialized" },
  { type: "system", text: "[SYSTEM] DevTools detection active" },
  { type: "warn", text: "[WARN]  Unauthorized access attempt detected" },
  { type: "info", text: "[INFO]  Source inspection is restricted" },
  { type: "info", text: "[INFO]  Right-click disabled on this domain" },
  { type: "error", text: "[ERROR] Access denied — portfolio protection enforced" },
  { type: "system", text: "[SYSTEM] Access logged. Session terminated." },
];

const DevToolsModal: React.FC<DevToolsModalProps> = ({ isOpen, onClose }) => {
  const [visibleLines, setVisibleLines] = useState(0);
  const terminalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setVisibleLines(0);
      return;
    }

    let lineIndex = 0;
    let cancelled = false;

    const lineInterval = setInterval(() => {
      lineIndex++;
      if (!cancelled) setVisibleLines(lineIndex);
      if (lineIndex >= terminalLines.length) clearInterval(lineInterval);
    }, 350);

    return () => {
      cancelled = true;
      clearInterval(lineInterval);
    };
  }, [isOpen]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [visibleLines]);

  const getLineColor = (type: string) => {
    switch (type) {
      case "error": return "text-red-400";
      case "warn": return "text-yellow-400";
      case "info": return "text-blue-400";
      case "system": return "text-green-400";
      default: return "text-gray-400";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Backdrop — solid, no click dismiss */}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

          {/* Terminal Window */}
          <motion.div
            className="relative w-full max-w-lg rounded-xl overflow-hidden shadow-2xl shadow-purple-500/10 border border-border/20"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } }}
            exit={{ scale: 0.9, opacity: 0, y: 20, transition: { duration: 0.2 } }}
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Title bar */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-[#1a1b26] border-b border-border/20">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80 cursor-pointer" onClick={onClose} />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <span className="text-[11px] font-mono text-textSecondary/40">
                evode@portfolio:~$ security
              </span>
              <button
                onClick={onClose}
                className="text-textSecondary/40 hover:text-textSecondary transition-colors"
              >
                {Icon(FaTimes, "text-xs")}
              </button>
            </div>

            {/* Terminal body */}
            <div
              ref={terminalRef}
              className="bg-[#0d1117] p-4 min-h-[260px] max-h-[320px] overflow-y-auto font-mono text-[12px] leading-relaxed"
              onContextMenu={(e) => e.preventDefault()}
            >
              {/* Prompt header */}
              <div className="flex items-center gap-2 mb-3 text-textSecondary/50">
                <span className="text-green-400">evode@portfolio</span>
                <span className="text-textSecondary/30">:</span>
                <span className="text-blue-400">~</span>
                <span className="text-textSecondary/30">$</span>
                <span className="text-textSecondary/40">run --security-check</span>
              </div>

              {/* Divider */}
              <div className="text-textSecondary/20 mb-3">
                {"─".repeat(48)}
              </div>

              {/* Terminal lines */}
              {terminalLines.slice(0, visibleLines).map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                  className={`mb-1 ${getLineColor(line.type)}`}
                >
                  {line.text}
                </motion.div>
              ))}

              {/* Cursor */}
              {visibleLines < terminalLines.length && (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-green-400">❯</span>
                  <div className="w-2 h-4 bg-green-400/80 animate-pulse" />
                </div>
              )}

              {/* Final prompt */}
              {visibleLines >= terminalLines.length && (
                <div className="flex items-center gap-1 mt-2 text-textSecondary/30">
                  <span className="text-green-400/60">❯</span>
                  <span className="text-textSecondary/20">_</span>
                </div>
              )}
            </div>

            {/* Close button at bottom */}
            <div className="px-4 py-3 bg-[#1a1b26] border-t border-border/20 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-1.5 rounded-lg text-[11px] font-mono font-semibold bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
              >
                Close Session
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DevToolsModal;
